import type { SalaryCalcData, SalaryCalcScheduleBracket } from '../../api/salaryCalc';
import type { SettlementType } from '../centerSettlement/types';

/**
 * 「구간 미설정」 판정 — 강사별 화면의 수업료 안내와 전체 강사 표의 배지가 같은 함수를 쓴다.
 * (SalaryCalculationPage 의 판정 블록(커밋 4b43791e)을 글자 그대로 옮겼다. 금액에는 쓰지 않는다 — 안내·배지 전용.)
 *
 * 구간 미설정 안내 — 매출 구간 표의 「구간 미설정」(구간 0개)이고 그쪽 수업이 있고 그쪽 수업료가 0원일 때.
 * 새로 계산하지 않고 서버가 준 건수·금액·적용 규칙 요약만 본다.
 * 적용 규칙 요약이 있으면 「구간표(tier)로 계산된 수업」만 근거로 삼는다 — 분류·재등록 예외·개별 요율의 0원은 설정일 수 있어
 * 안내하지 않는다. 요약은 IN/OUT 을 나누지 않으므로, 한쪽만 비었으면 「구간표 수업 수 − 다른 쪽(구간 있는) 수업 수 > 0」일 때만
 * 비어 있는 쪽에 구간표 수업이 반드시 있다고 본다(검증 P2 반복 — 한쪽 예외·개별 요율 조합 반례).
 */
export const computeBracketMissing = ({
    data,
    settlementType,
    inScheduleBrackets,
    outScheduleBrackets,
}: {
    data: Partial<SalaryCalcData> | undefined | null;
    settlementType: SettlementType | string;
    inScheduleBrackets: SalaryCalcScheduleBracket[];
    outScheduleBrackets: SalaryCalcScheduleBracket[];
}) => {
    const isTierSettlement = settlementType === 'tier';
    const appliedRuleSummary = settlementType === 'tier' ? data?.appliedRuleSummary ?? [] : [];
    const inScheduleCount = data?.inScheduleCount ?? 0;
    const outScheduleCount = data?.outScheduleCount ?? 0;
    const totalInScheduleFee = data?.totalInScheduleFee ?? 0;
    const totalOutScheduleFee = data?.totalOutScheduleFee ?? 0;
    const classFee = (data?.totalScheduleFee ?? 0) + (data?.groupScheduleFee ?? 0);

    const hasRuleSummary = appliedRuleSummary.length > 0;
    const tierScheduleCount = appliedRuleSummary
        .filter((item) => item.appliedRule === 'tier')
        .reduce((sum, item) => sum + (Number(item.scheduleCount) || 0), 0);
    const inBracketsEmpty = inScheduleBrackets.length === 0;
    const outBracketsEmpty = outScheduleBrackets.length === 0;

    let inBracketMissing = false;
    let outBracketMissing = false;
    let bracketMissingNotice: string | null = null;
    if (isTierSettlement && hasRuleSummary) {
        // 요약이 있으면 「구간표(tier)로 계산된 수업」만 근거로 삼는다(분류·재등록 예외·개별 요율 0원은 설정일 수 있다).
        // 요약은 IN/OUT 을 나누지 않는다 —
        //  · 양쪽 다 구간이 없으면: 구간표 수업이 있으면 그 수업은 반드시 0원. 어느 쪽인지 모르니 방향을 말하지 않는다(검증 P2 3회차).
        //  · 한쪽만 없으면: 구간표 수 − 다른 쪽(구간 있는) 수업 수 > 0 일 때만 비어 있는 쪽에 구간표 수업이 반드시 있다.
        //    그쪽 수업료에 예외 금액이 섞여 0원이 아닐 수 있으니 「그쪽 수업 중 일부」로 말한다.
        //  · 가릴 수 없으면 안내를 생략한다(수용한 한계).
        if (inBracketsEmpty && outBracketsEmpty) {
            const any = tierScheduleCount > 0;
            inBracketMissing = any;
            outBracketMissing = any;
            if (any) {
                bracketMissingNotice = classFee === 0
                    ? '구간이 설정되지 않아 수업료가 0원으로 계산됐어요'
                    : '구간이 설정되지 않아 0원으로 계산된 수업이 있어요';
            }
        } else if (inBracketsEmpty) {
            inBracketMissing = inScheduleCount > 0 && tierScheduleCount - outScheduleCount > 0;
            if (inBracketMissing) bracketMissingNotice = classFee === 0
                ? '구간이 설정되지 않아 수업료가 0원으로 계산됐어요'
                : '근무 시간 내 수업 중 구간이 설정되지 않아 0원으로 계산된 수업이 있어요';
        }
        // OUT 구간만 비어 있으면 미설정이 아니다 — 서버는 OUT 구간이 비면 IN 구간 요율로 계산한다
        // (rappo src/utils/salaryCalcAdjustment.js:50, 최종 검증 P2). IN 은 폴백이 없어 위 두 분기만 남긴다.
    } else if (isTierSettlement) {
        // 요약이 없는 옛 서버 — 그쪽 수업이 있고 그쪽 수업료가 0원이면.
        inBracketMissing = inBracketsEmpty && inScheduleCount > 0 && totalInScheduleFee === 0;
        // OUT 은 IN 구간으로 폴백되므로 IN 도 비었을 때만 미설정이다
        outBracketMissing = outBracketsEmpty && inBracketsEmpty && outScheduleCount > 0 && totalOutScheduleFee === 0;
        if (inBracketMissing || outBracketMissing) {
            bracketMissingNotice = classFee === 0
                ? '구간이 설정되지 않아 수업료가 0원으로 계산됐어요'
                : inBracketMissing && outBracketMissing
                  ? '구간이 설정되지 않아 0원으로 계산된 수업이 있어요'
                  : `${inBracketMissing ? '근무 시간 내' : '근무 시간 외'} 수업은 구간이 설정되지 않아 0원으로 계산됐어요`;
        }
    }
    return {
        inBracketMissing,
        outBracketMissing,
        isMissing: inBracketMissing || outBracketMissing,
        notice: bracketMissingNotice,
    };
};
